import gql from 'graphql-tag';
import { DesignationFields, DraftPickFields, FranchiseExpandedFields, FullTransactionFields, PlayerFields, SeasonsWithMetaFields, AuctionFields, AuctionItemFields } from './fragments';

// Queries

export const TRANSACTIONS_SEARCH = gql`
  query TransactionSearch($type: TransactionKind, $franchiseId:Int, $offset:Int, $status:String) {
    allTransactions(type: $type, franchiseId: $franchiseId, offset: $offset, status: $status) {
      total
      transactions {
        ...FullTransactionFields
      }
    }
  }
  ${FullTransactionFields}
`

export const DATA_QUERY = gql`
  query Data($chart: String!) {
    data(chart: $chart) {
      chart
      data
      colors
      keys
    }
  }
`

export const PLAYER_QUERY = gql`
  query Player($id:ID!, $includeHistory: Boolean = false) {
    player(id: $id) {
      __typename
      ...PlayerFields
      history @include(if: $includeHistory) {
        ...FullTransactionFields
      }
    }
  }
  ${FullTransactionFields}
`

export const ADJUSTMENT_QUERY = gql`
  query Adjustment($id:ID!, $includeHistory: Boolean = false) {
    adjustment(id: $id) {
      __typename
      id
      amount
      note
      history @include(if: $includeHistory) {
        ...FullTransactionFields
      }
    }
  }
  ${FullTransactionFields}
`

export const DRAFT_PICK_QUERY = gql`
  query DraftPick($id:ID!, $includeHistory: Boolean = false) {
    draftPick(id: $id) {
      __typename
      ...DraftPickFields
      history @include(if: $includeHistory) {
        ...FullTransactionFields
      }
    }
  }
  ${FullTransactionFields}
  ${DraftPickFields}
`

export const TRANSACTION_QUERY = gql`
  query Transaction($id:ID!) {
    transaction(id: $id) {
      ...FullTransactionFields
      season {
        year
      }
    }
  }
  ${FullTransactionFields}
`

export const OFFSEASON_NEGOTIATION_QUERY = gql`
  query OffseasonNegotiation($franchiseId: ID!, $season: Int!) {
    offseasonNegotiation(franchiseId: $franchiseId, season: $season) {
      ...FullTransactionFields
    }
  }
  ${FullTransactionFields}
`

export const OFFSEASON_NEGOTIATIONS_QUERY = gql`
  query OffseasonNegotiations($season: Int!) {
    offseasonNegotiations(season: $season) {      
      id
      type
      date
      status
      note
      franchise {
        id
        name
        logo
        abbr
      }
    }
  }
`

export const FAVORITES_QUERY = gql`
  query Favorites {
    favorites {
      id
    }
  }
`

export const FAVORITES_PLAYERS_QUERY = gql`
  query FavoritesPlayers {
    favorites {
      ...PlayerFields
    }
  }
  ${PlayerFields}
`

export const PLAYERS_QUERY = gql`
  query AllPlayers($search:String, $position:String, $offset:Int, $limit:Int $status:String, $contract:String, $favorites:Boolean, $rookies:Boolean, $sortOrder:String, $myRoster:Boolean) {
    allPlayers(search: $search, position: $position, offset: $offset, limit: $limit, status: $status, contract: $contract, favorites: $favorites, rookies: $rookies, sortOrder: $sortOrder, myRoster: $myRoster) {
      total
      players {
        ...PlayerFields
      }
    }
  }
  ${PlayerFields}
`

export const ALL_FRANCHISES_QUERY = gql`
  query Franchises {
    franchises {
      ...FranchiseExpandedFields
    }
  }
  ${FranchiseExpandedFields}
`

export const ALL_DESIGNATIONS_QUERY = gql`
  query Designations {
    designations {
      ...DesignationFields
    }
  }
  ${DesignationFields}
`

export const ALL_YEARS_QUERY = gql`
  query Years {
    years
  }
`

export const ALL_SEASONS_QUERY = gql`
  query Seasons {
    seasons {
      ...SeasonsWithMetaFields
    }
  }
  ${SeasonsWithMetaFields}
`;

export const ALL_DRAFT_PICKS_QUERY = gql`
  query DraftPicks($onlyAvailable:Boolean!, $franchiseId:ID, $year:Int) {
    draftPicks(onlyAvailable:$onlyAvailable, franchiseId:$franchiseId, year:$year) {
      ...DraftPickFields
    }
  }
  ${DraftPickFields}
`

export const FRANCHISE_QUERY = gql`
  query Franchise($id:ID!) {
    franchise(id: $id) {
      ...FranchiseExpandedFields
    }
  }
  ${FranchiseExpandedFields}
`

export const FRANCHISE_STATUS_QUERY = gql`
  query FranchiseStatus($id: ID!) {
    franchise(id: $id) {
      id
      logo
      availableCapSpace
      availableRosterSpots
    }
  }
`

export const AUCTION_QUERY = gql`
  query Auction {
    auction {
      ...AuctionFields
    }
  }
  ${AuctionFields}
`

export const DRAFT_HUB_FRANCHISES_QUERY = gql`
  query DraftHubFranchises {
    draftHubFranchises {
      id
      name
      abbr
      logo
      color
      availableCapSpace
      availableRosterSpots
      contracts {
        id
        salary
        designation {
          id
          name
        }
        player {
          id
          name
          position
          team
          adp
        }
      }
    }
  }
`

export const DRAFT_HUB_AUCTION_QUERY = gql`
  query DraftHubAuction {
    auction {
      id
      completedItems {
        player { id }
        leadingFranchise { id }
      }
    }
  }
`

// Mutations

export const LOGIN_USER = gql`
  mutation LoginUser($email: String!, $password: String!) {
    login(input: { email: $email, password: $password }) {
      token
      errors
    }
  }
`

export const CREATE_TRANSACTION = gql`
  mutation CreateTransaction($input: CreateTransactionInput!) {
    createTransaction(input: $input) {
      transaction {
        id
      }
      errors
    }
  }
`

export const APPROVE_TRANSACTION = gql`
  mutation ApproveTransaction($id:ID!) {
    approveTransaction(input: {id: $id}) {
      transaction {
        id
        type
        season {
          year
        }
      }
      errors
    }
  }
`

export const DECLINE_TRANSACTION = gql`
  mutation DeclineTransaction($id:ID!) {
    declineTransaction(input: {id: $id}) {
      transaction {
        id
      }
      errors
    }
  }
`

export const UPDATE_TRANSACTION_STATUS = gql`
  mutation UpdateTransactionStatus($id:ID!, $status:TransactionStatus!) {
    updateTransactionStatus(input: {id: $id, status: $status}) {
      transaction {
        ...FullTransactionFields
      }
      errors
    }
  }
  ${FullTransactionFields}
`

export const DEACTIVATE_ADJUSTMENT = gql`
  mutation DeactivateAdjustment($id:ID!) {
    deactivateAdjustment(input: {id: $id}) {
      adjustment {
        id
      }
      errors
    }
  }
`

export const CREATE_SEASON = gql`
  mutation CreateSeason($year: Int) {
    createSeason(input: { year: $year }) {
      season {
        id
        year
      }
      errors
    }
  }
`

export const CHANGE_SEASON = gql`
  mutation ChangeSeason($year: Int!) {
    changeSeason(input: { year: $year }) {
      season {
        id
        year
      }
      errors
    }
  }
`

export const CREATE_DRAFT = gql`
  mutation CreateDraft($year: Int!) {
    createDraft(input: { year: $year }) {
      season {
        id
        year
        hasDraft
        hasNegotiations
      }
      errors
    }
  }
`

export const SKIP_DRAFT_PICK = gql`
  mutation SkipDraftPick($input: SkipDraftPickInput!) {
    skipDraftPick(input: $input) {
      draftPick {
        id
      }
      errors
    }
  }
`

export const CLEAR_FAVORITES = gql`
  mutation ClearFavorites {
    clearFavorites(input: {}) {
      success
    }
  }
`

export const FAVORITE_PLAYER = gql`
  mutation FavoritePlayer($input: FavoritePlayerInput!) {
    favoritePlayer(input: $input) {
      player {
        id
      }
      errors
    }
  }
`



export const SAVE_FRANCHISE_QUERY = gql`
  mutation SaveFranchise($input: SaveFranchiseInput!) {
    saveFranchise(input: $input) {
      franchise {
        id
        name
        motto
        logo
        color
        abbr
        phone
      }
      errors
    }
  }
`

export const CREATE_TRANSACTION_ASSET = gql`
  mutation CreateTransactionAsset($input: CreateTransactionAssetInput!) {
    createTransactionAsset(input: $input) {
      transaction {
        ...FullTransactionFields
      }
      errors
    }
  }
  ${FullTransactionFields}
`

export const DELETE_TRANSACTION_ASSET = gql`
  mutation DeleteTransactionAsset($input: DeleteTransactionAssetInput!) {
    deleteTransactionAsset(input: $input) {
      transaction {
        ...FullTransactionFields
      }
      errors
    }
  }
  ${FullTransactionFields}
`

export const NOMINATE_PLAYER = gql`
  mutation NominatePlayer($playerId: ID!, $amount: Int) {
    nominatePlayer(input: { playerId: $playerId, amount: $amount }) {
      auctionItem {
        ...AuctionItemFields
      }
      errors
    }
  }
  ${AuctionItemFields}
`

export const CREATE_BID = gql`
  mutation CreateBid($amount: Int!, $idempotencyKey: String!, $auctionItemId: ID!) {
    createBid(input: { amount: $amount, idempotencyKey: $idempotencyKey, auctionItemId: $auctionItemId }) {
      auctionItem {
        ...AuctionItemFields
      }
      errors
    }
  }
  ${AuctionItemFields}
`

export const CANCEL_AUCTION_ITEM = gql`
  mutation CancelAuctionItem($auctionItemId: ID!) {
    cancelAuctionItem(input: { auctionItemId: $auctionItemId }) {
      auctionItem {
        ...AuctionItemFields
      }
      errors
    }
  }
  ${AuctionItemFields}
`

export const START_COUNTDOWN = gql`
  mutation StartCountdown($auctionItemId: ID!) {
    startCountdown(input: { auctionItemId: $auctionItemId }) {
      success
      errors
    }
  }
`

export const PAUSE_COUNTDOWN = gql`
  mutation PauseCountdown($auctionItemId: ID!) {
    pauseCountdown(input: { auctionItemId: $auctionItemId }) {
      success
      errors
    }
  }
`

export const SEND_AUCTION_REACTION = gql`
  mutation SendAuctionReaction($auctionId: ID!, $emoji: String!) {
    sendAuctionReaction(input: { auctionId: $auctionId, emoji: $emoji }) {
      success
    }
  }
`
